import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { User, Lock, Shield, CheckCircle, AlertCircle, Building2 } from 'lucide-react';

const AdminSetupPage: React.FC = () => {
  const { companySlug } = useParams<{ companySlug: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    name: '',
    password: '',
    confirmPassword: '',
    phone: '',
    twoFactorEnabled: false,
    twoFactorMethod: 'sms' as 'sms' | 'email' | 'authenticator'
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [invitationData, setInvitationData] = useState<any>(null);
  const [isLoadingInvitation, setIsLoadingInvitation] = useState(true);

  const token = searchParams.get('token');

  useEffect(() => {
    // Mock invitation validation - replace with actual API call
    const validateInvitation = async () => {
      setIsLoadingInvitation(true);
      try {
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Mock invitation data
        const mockInvitation = {
          id: 'inv_1',
          companyId: 'company_1',
          email: 'newadmin@dangote.com',
          role: 'hr_admin',
          department: 'HR',
          invitedBy: 'Admin User',
          expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(), // 24 hours from now
          status: 'pending'
        };
        
        setInvitationData(mockInvitation);
      } catch (error) {
        console.error('Failed to validate invitation:', error);
      } finally {
        setIsLoadingInvitation(false);
      }
    };

    if (token) {
      validateInvitation();
    } else {
      setIsLoadingInvitation(false);
    }
  }, [token]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors["name"] = 'Full name is required';
    }

    if (!formData.password) {
      newErrors["password"] = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors["password"] = 'Password must be at least 8 characters long';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
      newErrors["password"] = 'Password must contain uppercase, lowercase, and number';
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors["confirmPassword"] = 'Passwords do not match';
    }

    if (formData.phone && !/^[\+]?[1-9][\d]{0,15}$/.test(formData.phone.replace(/\s/g, ''))) {
      newErrors["phone"] = 'Please enter a valid phone number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Mock API call - replace with actual admin setup API
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Navigate to dashboard on success
      navigate(`/client/${companySlug}/admin/dashboard`);
    } catch (error) {
      console.error('Admin setup failed:', error);
      setErrors({ submit: 'Failed to complete setup. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  if (isLoadingInvitation) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Validating invitation...</p>
        </div>
      </div>
    );
  }

  if (!token || !invitationData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Invalid Invitation</h2>
          <p className="text-gray-600">This invitation link is invalid or has expired.</p>
          <button
            onClick={() => navigate(`/client/${companySlug}/admin/login`)}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  const isExpired = new Date(invitationData.expiresAt) < new Date();

  if (isExpired) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Invitation Expired</h2>
          <p className="text-gray-600">This invitation link has expired. Please contact your administrator for a new invitation.</p>
          <button
            onClick={() => navigate(`/client/${companySlug}/admin/login`)}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Left Side - Company Branding */}
      <div className="hidden lg:flex lg:w-1/2 relative" style={{ backgroundColor: '#0076b5' }}>
        <div className="absolute inset-0 bg-gradient-to-br from-[#004d7a] to-[#005a8c] opacity-90"></div>
        <div className="relative z-10 flex flex-col justify-center px-12 text-white">
          <div className="mb-8">
            <div className="w-16 h-16 bg-white bg-opacity-20 rounded-lg flex items-center justify-center mb-4">
              <Building2 className="h-8 w-8 text-white" />
            </div>
            <h1 className="text-4xl font-bold mb-2">Welcome to VPD Employer Portal</h1>
            <p className="text-xl text-blue-100">Complete your admin account setup</p>
          </div>
          
          <div className="space-y-6">
            <div className="flex items-center space-x-3">
              <CheckCircle className="h-5 w-5 text-green-300" />
              <span className="text-blue-100">Secure account creation</span>
            </div>
            <div className="flex items-center space-x-3">
              <CheckCircle className="h-5 w-5 text-green-300" />
              <span className="text-blue-100">Two-factor authentication setup</span>
            </div>
            <div className="flex items-center space-x-3">
              <CheckCircle className="h-5 w-5 text-green-300" />
              <span className="text-blue-100">Role-based access control</span>
            </div>
            <div className="flex items-center space-x-3">
              <CheckCircle className="h-5 w-5 text-green-300" />
              <span className="text-blue-100">Enterprise-grade security</span>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Setup Form */}
      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          {/* Mobile Company Header */}
          <div className="lg:hidden text-center mb-8">
            <div className="w-16 h-16 rounded-lg flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: '#0076b5' }}>
              <Building2 className="h-8 w-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Welcome to VPD Employer Portal</h1>
            <p className="text-gray-600">Complete your admin account setup</p>
          </div>

          {/* Invitation Details */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="text-sm font-medium text-blue-800 mb-2">Invitation Details</h3>
            <div className="text-sm text-blue-700 space-y-1">
              <p><strong>Email:</strong> {invitationData.email}</p>
              <p><strong>Role:</strong> {invitationData.role.replace('_', ' ').toUpperCase()}</p>
              <p><strong>Department:</strong> {invitationData.department}</p>
              <p><strong>Invited by:</strong> {invitationData.invitedBy}</p>
            </div>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              Complete Your Account Setup
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              Create your password and configure your account
            </p>
          </div>

          <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
            {errors["submit"] && (
              <div className="bg-red-50 border border-red-200 rounded-md p-4">
                <div className="flex">
                  <AlertCircle className="h-5 w-5 text-red-400" />
                  <div className="ml-3">
                    <p className="text-sm text-red-800">{errors["submit"]}</p>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-4">
              {/* Full Name */}
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name *
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <User className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    value={formData.name}
                    onChange={handleInputChange}
                    className={`w-full pl-10 pr-3 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      errors["name"] ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Enter your full name"
                  />
                </div>
                {errors["name"] && <p className="text-sm text-red-600 mt-1">{errors["name"]}</p>}
              </div>

              {/* Password */}
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                  Password *
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="password"
                    name="password"
                    type="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    className={`w-full pl-10 pr-3 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      errors["password"] ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Create a strong password"
                  />
                </div>
                {errors["password"] && <p className="text-sm text-red-600 mt-1">{errors["password"]}</p>}
                <p className="text-xs text-gray-500 mt-1">
                  Must be at least 8 characters with uppercase, lowercase, and number
                </p>
              </div>

              {/* Confirm Password */}
              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                  Confirm Password *
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    className={`w-full pl-10 pr-3 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      errors["confirmPassword"] ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Confirm your password"
                  />
                </div>
                {errors["confirmPassword"] && <p className="text-sm text-red-600 mt-1">{errors["confirmPassword"]}</p>}
              </div>

              {/* Phone Number */}
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number (Optional)
                </label>
                <input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors["phone"] ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Enter your phone number"
                />
                {errors["phone"] && <p className="text-sm text-red-600 mt-1">{errors["phone"]}</p>}
              </div>

              {/* 2FA Setup */}
              <div className="space-y-3">
                <div className="flex items-center">
                  <input
                    id="twoFactorEnabled"
                    name="twoFactorEnabled"
                    type="checkbox"
                    checked={formData.twoFactorEnabled}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="twoFactorEnabled" className="ml-2 block text-sm text-gray-900">
                    Enable two-factor authentication (recommended)
                  </label>
                </div>

                {formData.twoFactorEnabled && (
                  <div>
                    <label htmlFor="twoFactorMethod" className="block text-sm font-medium text-gray-700 mb-2">
                      2FA Method
                    </label>
                    <select
                      id="twoFactorMethod"
                      name="twoFactorMethod"
                      value={formData.twoFactorMethod}
                      onChange={handleInputChange}
                      className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="sms">SMS (Text Message)</option>
                      <option value="email">Email</option>
                      <option value="authenticator">Authenticator App</option>
                    </select>
                  </div>
                )}
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                style={{ backgroundColor: '#0076b5' }}
              >
                {isSubmitting ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Setting up account...
                  </div>
                ) : (
                  'Complete Setup'
                )}
              </button>
            </div>

            <div className="text-center">
              <p className="text-sm text-gray-600">
                By completing setup, you agree to our{' '}
                <a href="#" className="font-medium text-blue-600 hover:text-blue-500">
                  Terms of Service
                </a>{' '}
                and{' '}
                <a href="#" className="font-medium text-blue-600 hover:text-blue-500">
                  Privacy Policy
                </a>
              </p>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AdminSetupPage;
